import { z } from 'zod';
import { IdSchema, IsoDateSchema, SOURCE_MAX_FILES, SourcePathsSchema } from './common.js';
/** MoSCoW priority for a user story — anchors the priority of the tasks that trace to it. */
export const STORY_PRIORITIES = ['must', 'should', 'could'];
export const StoryPrioritySchema = z.enum(STORY_PRIORITIES);
/** One measurable outcome the project commits to. `metric` and `target` are optional so a goal
 * can be written down before anyone has decided how to measure it. */
export const PrdGoalSchema = z.object({
    goal: z.string().min(1).max(500),
    metric: z.string().min(1).max(300).optional(),
    target: z.string().min(1).max(200).optional(),
});
const PrdListItemSchema = z.string().min(1).max(500);
/** The optional PRD sections added after the original concept (goals, non-functional
 * requirements, assumptions, dependencies, risks, open questions). Every one is optional rather
 * than defaulted: versions are immutable, so documents saved before these fields existed must
 * keep parsing, and readers must treat a missing field as "not stated". */
const PrdOptionalSectionsShape = {
    goals: z.array(PrdGoalSchema).optional(),
    nonFunctionalRequirements: z.array(PrdListItemSchema).optional(),
    assumptions: z.array(PrdListItemSchema).optional(),
    dependencies: z.array(PrdListItemSchema).optional(),
    risks: z.array(PrdListItemSchema).optional(),
    openQuestions: z.array(PrdListItemSchema).optional(),
};
/** A key user story. `id` is server-assigned and never reused, so tasks can trace back to it
 * even after the story is reworded or reordered. See docs/PLAN.md §5.1. */
export const UserStorySchema = z.object({
    id: IdSchema, // e.g. "US-01"
    role: z.string().min(1).max(200),
    want: z.string().min(1).max(1000),
    benefit: z.string().min(1).max(1000),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).optional(),
    priority: StoryPrioritySchema.optional(),
});
/** The structured content of one PRD version. This is what lives in
 * projects/{id}/documents/prd/versions/{versionId}.content */
export const PrdContentSchema = z.object({
    projectName: z.string().min(1).max(200),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema.nullable().default(null),
    projectLeadUid: IdSchema,
    problemStatement: z.string().min(1).max(5000),
    targetAudience: z.array(z.string().min(1).max(200)).min(1),
    userStories: z.array(UserStorySchema).min(1),
    outOfScope: z.array(z.string().min(1).max(500)).default([]),
    ...PrdOptionalSectionsShape,
});
/** Coerces a model-written date to YYYY-MM-DD, or drops it. Gemini's structured output doesn't
 * reliably honour the schema's regex `pattern`, and returns "", full timestamps or prose like
 * "Q1 2027" — none of which should fail a whole generation over an optional field. */
export function normalizeLlmDate(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    const isoPrefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (isoPrefix) {
        // Round-trip so a placeholder like "0000-00-00" or an impossible "2026-02-31" is dropped
        // rather than reaching the date picker as an unrenderable value.
        const [, y, m, d] = isoPrefix.map(Number);
        const date = new Date(Date.UTC(y, m - 1, d));
        const valid = y >= 1900 && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
        return valid ? isoPrefix[0] : undefined;
    }
    const parsed = trimmed ? new Date(trimmed) : null;
    if (!parsed || Number.isNaN(parsed.getTime()) || parsed.getFullYear() < 1900)
        return undefined;
    const pad = (n) => String(n).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}
const LlmDateSchema = z.preprocess(normalizeLlmDate, IsoDateSchema.optional());
/** What the LLM returns from document extraction — same shape as PrdContent but nearly every
 * field is optional (a poorly-written source doc may not yield everything) and story/audience
 * ids are not yet assigned; the API assigns them on save. The problem statement is the
 * exception: as an optional field Gemini's structured output routinely left it out, and any PRD
 * worth uploading supports at least a summary of what it's solving. */
export const PrdExtractionResultSchema = z.object({
    projectName: z.string().max(200).optional(),
    startDate: LlmDateSchema,
    endDate: LlmDateSchema,
    problemStatement: z.string().min(1).max(5000),
    targetAudience: z.array(z.string().max(200)).optional(),
    userStories: z
        .array(z.object({
        role: z.string().max(200),
        want: z.string().max(1000),
        benefit: z.string().max(1000),
        acceptanceCriteria: z.array(z.string().max(500)).optional(),
        priority: StoryPrioritySchema.optional(),
    }))
        .optional(),
    outOfScope: z.array(z.string().max(500)).optional(),
    goals: z
        .array(z.object({ goal: z.string().max(500), metric: z.string().max(300).optional(), target: z.string().max(200).optional() }))
        .optional(),
    nonFunctionalRequirements: z.array(z.string().max(500)).optional(),
    assumptions: z.array(z.string().max(500)).optional(),
    dependencies: z.array(z.string().max(500)).optional(),
    risks: z.array(z.string().max(500)).optional(),
    openQuestions: z.array(z.string().max(500)).optional(),
});
/** A user story as submitted by the client when saving a draft: `id` is optional because
 * story ids are server-assigned (see UserStorySchema above) — a brand-new story the user just
 * typed in the form has no id yet, while a story already in a previous version keeps the id it
 * was given so tasks that trace to it stay linked. The API assigns ids to any story missing one
 * when it saves the version — see saveDocumentVersion's caller in routes/documents.ts. */
export const SavePrdUserStorySchema = z.object({
    id: IdSchema.optional(),
    role: z.string().min(1).max(200),
    want: z.string().min(1).max(1000),
    benefit: z.string().min(1).max(1000),
    acceptanceCriteria: z.array(z.string().min(1).max(500)).optional(),
    priority: StoryPrioritySchema.optional(),
});
export const SavePrdDraftInputSchema = PrdContentSchema.omit({ userStories: true, outOfScope: true })
    .partial({ endDate: true })
    .extend({
    userStories: z.array(SavePrdUserStorySchema).min(1).optional(),
    outOfScope: z.array(z.string().min(1).max(500)).optional(),
    changeSummary: z.string().max(500).optional(),
});
export const AcceptDocumentInputSchema = z.object({
    versionId: IdSchema,
});
/** How many follow-up rounds the PRD brainstorm may run before it must produce a draft. Kept
 * small: the brainstorm is a quick way to a first draft, not a full discovery interview. */
export const PRD_BRAINSTORM_MAX_ROUNDS = 3;
/** One follow-up question the LLM asks while brainstorming a PRD from a short description. */
export const PrdBrainstormQuestionSchema = z.object({
    id: IdSchema, // unique within its round, e.g. "q1"
    prompt: z.string().min(1).max(500),
    helpText: z.string().max(500).optional(),
    suggestedOptions: z
        .array(z.object({ label: z.string().min(1).max(200), description: z.string().max(300).optional() }))
        .max(5)
        .default([]),
});
/** A completed round: the questions asked and whatever the user answered. A question left
 * unanswered is simply absent from `answers` — the user chose to skip it. */
export const PrdBrainstormRoundSchema = z.object({
    clarification: z.string().max(1000).optional(),
    questions: z.array(PrdBrainstormQuestionSchema).min(1).max(8),
    answers: z.array(z.object({ questionId: IdSchema, answer: z.string().min(1).max(1000) })),
});
/** How many source documents one extraction or brainstorm may attach. */
export const PRD_SOURCE_MAX_FILES = SOURCE_MAX_FILES;
/** Storage paths of PRD source files already uploaded via POST .../prd/upload-url. */
const PrdSourcePathsSchema = SourcePathsSchema;
/** POST /projects/:id/prd/extract — Gemini reads every file together into one draft. */
export const PrdExtractInputSchema = z.object({
    storagePaths: PrdSourcePathsSchema.min(1),
});
/** The shortest description the brainstorm will start from when no documents are attached. */
export const PRD_BRAINSTORM_MIN_DESCRIPTION = 10;
/** POST /projects/:id/prd/brainstorm. The client holds the conversation and resends it whole
 * each round; `finish` asks for the draft now instead of more questions. Attached documents
 * stand in for — or add to — the description, so either one is enough to start. */
export const PrdBrainstormInputSchema = z
    .object({
    description: z.string().trim().max(4000).default(''),
    sourcePaths: PrdSourcePathsSchema.default([]),
    rounds: z.array(PrdBrainstormRoundSchema).max(PRD_BRAINSTORM_MAX_ROUNDS).default([]),
    finish: z.boolean().default(false),
})
    .refine((input) => input.sourcePaths.length > 0 || input.description.length >= PRD_BRAINSTORM_MIN_DESCRIPTION, {
    message: `Describe the project in at least ${PRD_BRAINSTORM_MIN_DESCRIPTION} characters or attach a document`,
    path: ['description'],
});
/** The drafted PRD a brainstorm returns. Unlike extraction (where a thin source doc may really
 * lack a section), the brainstorm writes the draft itself, so the core sections are required:
 * with every field optional, Gemini's structured output routinely returned only a name, audience
 * and stories. The lists may still be empty when there is honestly nothing to put in them. */
export const PrdBrainstormDraftSchema = PrdExtractionResultSchema.extend({
    problemStatement: z.string().min(1).max(5000),
    targetAudience: z.array(z.string().max(200)).min(1),
    userStories: PrdExtractionResultSchema.shape.userStories.unwrap().min(1),
    goals: PrdExtractionResultSchema.shape.goals.unwrap(),
    outOfScope: z.array(z.string().max(500)),
    nonFunctionalRequirements: z.array(z.string().max(500)),
    assumptions: z.array(z.string().max(500)),
    risks: z.array(z.string().max(500)),
    openQuestions: z.array(z.string().max(500)),
});
/** What one brainstorm round returns: either more questions (with an optional clarification
 * note when the description is vague or reads two ways) or the drafted PRD. A flat object
 * rather than a union, since a union's anyOf is the part of structured output models handle
 * least reliably — the API checks the right half is present for the status. */
export const PrdBrainstormResultSchema = z.object({
    status: z.enum(['needs_answers', 'ready']),
    clarification: z.string().max(1000).optional(),
    questions: z.array(PrdBrainstormQuestionSchema).max(8).optional(),
    prd: PrdBrainstormDraftSchema.optional(),
});
//# sourceMappingURL=prd.js.map